import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

const CLOSED_HEIGHT_IN_PX = 64;
const OPENED_MAX_HEIGHT_IN_PX = 232;

const SWITCH_CONTAINER_MARGIN_TOP_IN_PX = 16;
const SWITCH_CONTAINER_ROW_GAP_IN_PX = 8;
const ACCOUNT_ROW_HEIGHT_IN_PX = 40;
const BOTTOM_ROW_PADDING_IN_PX = 16;

/**
 * Height left for the scrollable list once the two fixed rows are laid out (current
 * account on top, logout at the bottom), so the opened section keeps its original max
 * height. The bottom row padding is not subtracted: Bootstrap sets border-box globally.
 */
const SCROLLABLE_LIST_MAX_HEIGHT_IN_PX =
  OPENED_MAX_HEIGHT_IN_PX -
  SWITCH_CONTAINER_MARGIN_TOP_IN_PX -
  2 * ACCOUNT_ROW_HEIGHT_IN_PX -
  2 * SWITCH_CONTAINER_ROW_GAP_IN_PX;

const Container = styled.div`
  padding: 0 16px;
  display: flex;
  height: ${({ hasMultipleAccounts, isSwitchAccountOpened }) =>
    (!hasMultipleAccounts || !isSwitchAccountOpened) && `${CLOSED_HEIGHT_IN_PX}px`};
  flex-direction: ${({ hasMultipleAccounts }) => (!hasMultipleAccounts ? 'row' : 'column')};
  justify-content: space-between;
  align-items: ${({ hasMultipleAccounts }) => (!hasMultipleAccounts ? 'center' : 'flex-start')};

  background: ${(props) => props.theme.colors.greys.darkest};

  ${({ hasMultipleAccounts }) => {
    if (hasMultipleAccounts) {
      return css`
        transition-property: max-height;
        transition-duration: 100ms;
      `;
    }
  }}

  /** Keep max-height property after transition properties */
  max-height: ${({ hasMultipleAccounts, isSwitchAccountOpened }) =>
    !hasMultipleAccounts || !isSwitchAccountOpened
      ? `${CLOSED_HEIGHT_IN_PX}px`
      : `${OPENED_MAX_HEIGHT_IN_PX}px`};

  ::before {
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: ${({ hasMultipleAccounts }) => !hasMultipleAccounts && `${CLOSED_HEIGHT_IN_PX}px`};
    border-bottom: solid 1px ${(props) => props.theme.colors.greys.darker};
  }
`;

const UserContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const SwichContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${SWITCH_CONTAINER_MARGIN_TOP_IN_PX}px;
  row-gap: ${SWITCH_CONTAINER_ROW_GAP_IN_PX}px;
  width: 100%;
`;

const ScrollableAccountList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${SWITCH_CONTAINER_ROW_GAP_IN_PX}px;
  max-height: ${SCROLLABLE_LIST_MAX_HEIGHT_IN_PX}px;
  overflow-y: auto;
`;

const AccountContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: ${ACCOUNT_ROW_HEIGHT_IN_PX}px;
  justify-content: space-between;
  padding-bottom: ${({ isBottom }) => isBottom && `${BOTTOM_ROW_PADDING_IN_PX}px`};
  align-items: center;

  > a:hover {
    text-decoration: none;
  }

  /** On hover, color all div text inside this component */
  &:hover > div > div {
    color: ${({ theme }) => theme.colors.infoGreen};
  }

  :hover {
    cursor: pointer;
  }

  &:hover > img {
    -webkit-filter: url(#colorize);
    filter: url(#colorize);
  }
`;

const Icon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;

  transition: filter 0.3s; /* Add a smooth transition effect */

  &:hover {
    -webkit-filter: url(#colorize);
    filter: url(#colorize);
  }
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  > a:hover {
    text-decoration: none;
  }
`;

const StyledLink = styled(Link)`
  &:hover {
    text-decoration: none;
  }
`;

const textWithEllipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export {
  Container,
  UserContainer,
  SwichContainer,
  ScrollableAccountList,
  AccountContainer,
  NameContainer,
  Icon,
  textWithEllipsis,
  StyledLink,
};
